import React, { useEffect } from "react"
import { EditorContent, useEditor } from "@tiptap/react"
import { migrateMathStrings } from "@tiptap/extension-mathematics"

import { createTiptapExtensions } from "../tiptapExtensions"
import { applyExplicitImageDimensions } from "../utils/applyExplicitImageDimensions"
import { sanitizeEditorHtml } from "../utils/sanitizeEditorHtml"
import "katex/dist/katex.min.css"
import "katex/contrib/mhchem"
import "../tiptapEditor.scss"

type TiptapReadOnlyContentProps = {
  html: string
  className?: string
}

const syncReadOnlyContent = (editor: NonNullable<ReturnType<typeof useEditor>>) => {
  migrateMathStrings(editor)
  applyExplicitImageDimensions(editor.view.dom)
}

const sanitizeReadOnlyHtml = sanitizeEditorHtml

const TiptapReadOnlyContent = React.memo(
  ({ html, className = "" }: TiptapReadOnlyContentProps) => {
    const sanitizedHtml = sanitizeReadOnlyHtml(html)

    const editor = useEditor({
      extensions: createTiptapExtensions({ readOnly: true }),
      content: sanitizedHtml,
      editable: false,
      immediatelyRender: false,
      shouldRerenderOnTransaction: false,
      onCreate: ({ editor: currentEditor }) => {
        syncReadOnlyContent(currentEditor)
      },
    })

    useEffect(() => {
      if (!editor) return
      if (editor.getHTML() === sanitizedHtml) return

      editor.commands.setContent(sanitizedHtml, { emitUpdate: false })
      syncReadOnlyContent(editor)
    }, [editor, sanitizedHtml])

    if (!editor) return null

    return (
      <div
        className={`tiptap-editor tiptap-editor--readonly${className ? ` ${className}` : ""}`}
      >
        <div className='tiptap-editor__content tiptap-editor__content--readonly'>
          <EditorContent editor={editor} />
        </div>
      </div>
    )
  }
)

TiptapReadOnlyContent.displayName = "TiptapReadOnlyContent"

export default TiptapReadOnlyContent
